import { memo } from 'react'
import { Handle, NodeToolbar, Position } from '@xyflow/react'
import { getNodeColorClasses } from '@/lib/node-colors'
import { Button } from '@/components/ui/button'
import { NodeIcon } from '@/components/ui/NodeIcon'
import { Palette, Trash2, Link2Off, Zap } from 'lucide-react'

// React Flow Tailwind-styled node component
// Icon = Workflow Node icon (Product icon)
// Name = Workflow Node name (Product name)
// Subtitle = Brand/Provider of the Product
export default memo(function ActionNode({ data, selected, sourcePosition, targetPosition }: any) {
  const color = data?.color
  const c = getNodeColorClasses(color)
	return (
		<div className="relative group">
			<div
				className={`
					px-4 py-3
					shadow-md rounded-lg
					bg-white dark:bg-gray-800
					border-2
					transition-all duration-200
					hover:shadow-xl
					${selected ? `ring-2 ${c.ring} ${c.border}` : `border-gray-300 dark:border-gray-600`}
				`}
			>
				{/* Color accent bar at top */}
				<div className={`absolute top-0 left-0 right-0 h-2 rounded-t-lg ${c.bar}`} />

				<div className="mt-2 flex items-center gap-3">
					{/* Product Icon - Square with rounded corners */}
					<div
						className={`
							rounded-lg
							w-12 h-12
							flex justify-center items-center
							${c.bar} bg-opacity-10
							border-2 border-current border-opacity-20
							flex-shrink-0
							transition-transform duration-200
							group-hover:scale-105
						`}
					>
						<NodeIcon
							icon={data?.icon}
							size="xl"
							fallback={<Zap className="w-6 h-6 text-current" />}
						/>
					</div>

					{/* Product Name and Brand */}
					<div className="flex flex-col gap-0.5 flex-1 min-w-0">
						<div className="font-bold text-base text-gray-900 dark:text-gray-100 truncate">
							{data?.label || data?.product?.name || 'Action'}
						</div>
						<div className="text-sm text-gray-500 dark:text-gray-400 truncate">
							{data?.brand?.name || data?.vendor || 'Processor'}
						</div>
					</div>
				</div>
			</div>

			{/* Connection handles */}
			<Handle
				type="target"
				position={targetPosition ?? Position.Left}
				className="w-3 h-3 !bg-gray-400 dark:!bg-gray-600 border-2 border-white dark:border-gray-800 transition-colors hover:!bg-gray-600"
			/>
			<Handle
				type="source"
				position={sourcePosition ?? Position.Right}
				className="w-3 h-3 !bg-gray-400 dark:!bg-gray-600 border-2 border-white dark:border-gray-800 transition-colors hover:!bg-gray-600"
			/>

			{/* Node toolbar */}
			<NodeToolbar isVisible={selected} position={Position.Top} className="flex gap-1">
				<Button size="sm" variant="outline" className="h-7 px-2 shadow-md" onClick={data?.onCycleColor}>
					<Palette className="h-3 w-3 mr-1" /> Color
				</Button>
				{data?.onDetach && (
					<Button size="sm" variant="outline" className="h-7 px-2 shadow-md" onClick={data.onDetach}>
						<Link2Off className="h-3 w-3 mr-1" /> Detach
					</Button>
				)}
				{data?.onDelete && (
					<Button size="sm" variant="destructive" className="h-7 px-2 shadow-md" onClick={data.onDelete}>
						<Trash2 className="h-3 w-3 mr-1" /> Delete
					</Button>
				)}
			</NodeToolbar>
		</div>
	)
})